import { useState, type FormEvent } from 'react';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { Select } from '../ui/select';

export type ValueEditor =
  | { kind: 'number'; value: number; min: number; max: number; unit?: string }
  | { kind: 'text'; value: string; placeholder?: string; maxLength?: number }
  | { kind: 'select'; value: string; options: { value: string; label: string }[] };

export interface ValueEditorDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  label: string;
  editor: ValueEditor;
  /** Receives the new value, as a number for `number` editors. */
  onSave: (value: string | number) => void;
}

function Body({ label, editor, onClose, onSave }: Omit<ValueEditorDialogProps, 'open' | 'title' | 'description'>) {
  const [raw, setRaw] = useState(String(editor.value));
  const [error, setError] = useState<string>();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (editor.kind === 'number') {
      const n = Number(raw);
      if (!Number.isInteger(n) || n < editor.min || n > editor.max) {
        setError(`Enter a whole number from ${editor.min} to ${editor.max}`);
        return;
      }
      onSave(n);
    } else {
      if (editor.kind === 'text' && raw.trim() === '') {
        setError('This can’t be empty');
        return;
      }
      onSave(editor.kind === 'text' ? raw.trim() : raw);
    }
    onClose();
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-(--mbm-space-5)">
      {editor.kind === 'select' ? (
        <Select variant="filled" label={label} value={raw} onChange={(e) => setRaw(e.target.value)}>
          {editor.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          variant="filled"
          label={label}
          autoFocus
          inputMode={editor.kind === 'number' ? 'numeric' : undefined}
          maxLength={editor.kind === 'text' ? editor.maxLength : undefined}
          placeholder={editor.kind === 'text' ? editor.placeholder : undefined}
          value={raw}
          error={error}
          helperText={editor.kind === 'number' && editor.unit ? editor.unit : undefined}
          onChange={(e) => {
            setRaw(e.target.value);
            setError(undefined);
          }}
        />
      )}
      <div className="grid grid-cols-2 gap-(--mbm-space-3)">
        <Button variant="secondary" size="compact" onClick={onClose}>
          Cancel
        </Button>
        <Button size="compact" type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}

/** One small dialog for every "value + chevron" row (limits, scheduling rules, chat name...). */
export function ValueEditorDialog({
  open,
  onClose,
  title,
  description,
  label,
  editor,
  onSave,
}: ValueEditorDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} description={description}>
      {open ? <Body label={label} editor={editor} onClose={onClose} onSave={onSave} /> : null}
    </Dialog>
  );
}
